import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Bell,
  CheckCheck,
  CheckCircle2,
  ClipboardList,
  FolderKanban,
  type LucideIcon,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationAsReadMutation,
} from '@/features/notifications/notificationsApi.js';
import { Button } from '@/components/ui/button.js';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import { Separator } from '@/components/ui/separator.js';
import { cn } from '@/lib/utils.js';
import type { NotificationDropdownProps } from './NotificationDropdown.types.js';

const TABS = [
  { key: '', label: 'Все' },
  { key: 'task', label: 'Задачи' },
  { key: 'team', label: 'Команды' },
  { key: 'project', label: 'Проекты' },
  { key: 'approval', label: 'Согласование' },
] as const;

const CATEGORY_META: Record<string, { icon: LucideIcon; iconClass: string; bgClass: string }> = {
  task: { icon: ClipboardList, iconClass: 'text-primary', bgClass: 'bg-primary/10' },
  approval: { icon: ShieldCheck, iconClass: 'text-warning', bgClass: 'bg-warning/10' },
  team: { icon: Users, iconClass: 'text-success', bgClass: 'bg-success/10' },
  project: { icon: FolderKanban, iconClass: 'text-info', bgClass: 'bg-info/10' },
  system: { icon: Sparkles, iconClass: 'text-muted-foreground', bgClass: 'bg-muted' },
  message: { icon: MessageCircle, iconClass: 'text-accent', bgClass: 'bg-accent/10' },
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'только что';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин назад`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ч назад`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} дн назад`;
  return new Date(dateStr).toLocaleDateString('ru-RU');
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const navigate = useNavigate();
  const [tabKey, setTabKey] = useState<string>('');
  const category = tabKey || undefined;

  const { data, isLoading } = useGetNotificationsQuery({ limit: 30, category });
  const [markAsRead] = useMarkNotificationAsReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();

  const notifications = data?.data?.notifications ?? [];
  const unreadCount = notifications.filter((n: any) => !n.isRead).length;

  const handleClick = async (notif: any) => {
    if (!notif.isRead) {
      try {
        await markAsRead(notif.id);
      } catch {
        // ignore
      }
    }
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
      onClose();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead(category);
    } catch {
      // ignore
    }
  };

  return (
    <div className="flex h-[500px] max-h-[80vh] flex-col overflow-hidden rounded-md card-glass !p-0">
      {/* Header */}
      <header className="flex items-center justify-between gap-2 px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
            <Bell className="h-4 w-4" />
          </span>
          <h3 className="font-display text-base font-semibold">Уведомления</h3>
          {unreadCount > 0 && (
            <span className="grid h-5 min-w-[20px] place-items-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground">
              {unreadCount}
            </span>
          )}
        </div>
        {notifications.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={handleMarkAllRead}
            disabled={unreadCount === 0}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Прочитать все
          </Button>
        )}
      </header>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto px-3 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((tab) => {
          const active = tab.key === tabKey;
          return (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setTabKey(tab.key)}
              className={cn(
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <Separator className="bg-border-subtle" />

      {/* Body */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3 p-2">
                <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-muted" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                  <div className="h-2 w-1/3 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-muted/50 text-muted-foreground">
              <Bell className="h-6 w-6" />
            </span>
            <p className="text-small font-medium">Пока нет уведомлений</p>
            <p className="text-xs text-muted-foreground">Новые сообщения и задачи появятся здесь</p>
          </div>
        ) : (
          <ul className="p-1.5">
            <AnimatePresence initial={false}>
              {notifications.map((notif: any, idx: number) => {
                const meta = CATEGORY_META[notif.category] ?? CATEGORY_META.system!;
                const Icon = meta.icon;
                return (
                  <motion.li
                    key={notif.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0, transition: { delay: idx * 0.02 } }}
                    exit={{ opacity: 0, x: -10 }}
                  >
                    <button
                      type="button"
                      onClick={() => handleClick(notif)}
                      className={cn(
                        'group flex w-full items-start gap-3 rounded-lg p-2.5 text-left transition-colors',
                        'hover:bg-accent/10',
                        !notif.isRead && 'bg-primary/[0.06]',
                      )}
                    >
                      <span
                        className={cn(
                          'grid h-9 w-9 shrink-0 place-items-center rounded-lg',
                          meta.bgClass,
                          meta.iconClass,
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2">
                          <p
                            className={cn(
                              'text-small leading-snug',
                              !notif.isRead
                                ? 'font-semibold text-foreground'
                                : 'font-normal text-muted-foreground',
                            )}
                          >
                            {notif.title}
                          </p>
                          {!notif.isRead && (
                            <span
                              className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                              aria-label="Не прочитано"
                            />
                          )}
                        </div>
                        {notif.body && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {notif.body}
                          </p>
                        )}
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {timeAgo(notif.createdAt)}
                        </p>
                      </div>
                    </button>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        )}
      </ScrollArea>

      {notifications.length > 0 && (
        <>
          <Separator className="bg-border-subtle" />
          <footer className="flex items-center justify-between gap-2 px-4 py-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Показано: {notifications.length}
            </span>
          </footer>
        </>
      )}
    </div>
  );
}
